import React, {
  forwardRef,
  FunctionComponent,
  useCallback,
  useRef,
  useState,
} from 'react'
import { ApiProduct } from '@vend/types'
import {
  Autocomplete,
  Icon,
  PopoverListFooter,
  PopoverListFooterAction,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { Currency } from '@vend/business-components'
import { ProductBadge } from '@vend/react-utilities'
import { fetchApi } from '@vend/utilities'
import { T, useT, UT } from '@transifex/react'
import {
  useRetailerSettingsManager,
  useSaleTransformer,
} from '../../ng-compat/hooks'
import { ProductDetails } from '../AddProductToSale/useAddProductToSale'
import { $rootScope } from '../../ng-compat/imports'
import { getFeatureFromCache } from '../../util/feature-cache'
import { FEATURE_BARCODE_SCAN_ERROR_FIX } from '../../constants'

export interface WrReactProductSearchProps {
  onProductSelected: (
    product: ProductDetails | null,
    inputValue: string
  ) => void
  onAddNewProduct?: (searchTerm: string) => void
  feelingLucky?: (
    query: string,
    suggestionsAvailable: boolean
  ) => Promise<{ handled: boolean; product: ApiProduct | undefined }>
  ref?: React.ForwardedRef<Autocomplete<HydratedSuggestion>>
}

export interface HydratedSuggestion {
  type: string
  id: string
  name: string
  product: ApiProduct
}

const SUGGEST_URL = '/api/2.0/suggest?type=product&type=variant&page_size=30'

const GIFT_CARD_SKU = 'vend-internal-gift-card'

const isHydratedSuggestion = (
  suggestion: HydratedSuggestion | undefined
): suggestion is HydratedSuggestion => Boolean(suggestion)

const isExactMatch = (suggestion: HydratedSuggestion, query: string) => {
  return suggestion.name.toLowerCase() === query.toLowerCase()
}

const productSuggestionToDetails = (suggestion: HydratedSuggestion) => ({
  label: suggestion.name,
  productId: suggestion.id,
  isChild: suggestion.type === 'variant',
})

const getProductForBadge = (suggestion: HydratedSuggestion) => ({
  sku: suggestion.product.sku,
  name: suggestion.name,
  hasVariants: suggestion.product.has_variants,
  variantParentID: suggestion.product.variant_parent_id,
  variantName: suggestion.product.variant_name,
  isComposite: suggestion.product.is_composite,
  variantCount: suggestion.product.variant_count,
  imageThumbnailURL: suggestion.product.image_thumbnail_url,
  skuImageThumbnailURL: suggestion.product.skuImages?.[0]?.sizes.thumb,
})

const ProductSuggestion: FunctionComponent<{
  suggestion: HydratedSuggestion
}> = ({ suggestion }) => {
  const retailer = useRetailerSettingsManager().get()
  return (
    <div className="vd-flex vd-flex--row vd-flex vd-flex--justify-between">
      <ProductBadge
        product={getProductForBadge(suggestion)}
        variant={suggestion.type === 'variant'}
      />
      {suggestion.product.sku === GIFT_CARD_SKU ? null : (
        <div className="wr-product-suggestion-price vd-align-right">
          <Currency
            value={
              retailer.tax_exclusive
                ? suggestion.product.price_excluding_tax
                : suggestion.product.price_including_tax
            }
          />
        </div>
      )}
    </div>
  )
}

const NetworkError: FunctionComponent = () => (
  <div
    data-testid="network-error-message"
    className="vd-popover-list-empty vd-align-center"
  >
    <p className="vd-p">
      <UT
        _str="We’re currently unable to search your&nbsp;products.<br>
  You can still add products to the sale using the barcode&nbsp;or&nbsp;SKU."
        _context="Error message displayed when we can't search for products"
        _inline
      />
    </p>
  </div>
)

export const WrReactProductSearch = forwardRef<
  Autocomplete<HydratedSuggestion>,
  WrReactProductSearchProps
>(({ feelingLucky, onProductSelected, onAddNewProduct }, forwardedRef) => {
  const t = useT()
  const saleTransformer = useSaleTransformer()
  const barcodeScanErrorFix = getFeatureFromCache(
    FEATURE_BARCODE_SCAN_ERROR_FIX
  )
  const hasSuggestions = useRef(false)
  const lastFetchedQuery = useRef('')
  const feelingLuckyHandledQuery = useRef<string | boolean | null>(false)
  const [currentQuery, setCurrentQuery] = useState('')
  const [lastInputValue, setLastInputValue] = useState('')
  const [exactMatch, setExactMatch] = useState(false)
  const [networkError, setNetworkError] = useState(false)

  const fetchSuggestions = useCallback(
    async (inputValue: string): Promise<HydratedSuggestion[]> => {
      setLastInputValue(inputValue)
      setNetworkError(false)
      let responseData: {
        data: Array<{ id: string; type: string }>
      }
      try {
        const response = await fetchApi(
          `${SUGGEST_URL}&q=${encodeURIComponent(inputValue)}`
        )
        responseData = await response.json()
      } catch (e) {
        setNetworkError(true)
        return []
      }
      const resultList = responseData.data
      const hydratedList: Array<
        HydratedSuggestion | undefined
      > = await Promise.all(
        resultList.map(productSuggestion =>
          saleTransformer.hydrateProduct(productSuggestion.id).then(product =>
            product
              ? {
                  ...productSuggestion,
                  product,
                  name:
                    productSuggestion.type === 'variant'
                      ? product.variant_name
                      : product.name,
                }
              : undefined
          )
        )
      )
      const filteredList = hydratedList.filter(isHydratedSuggestion)
      if (barcodeScanErrorFix) {
        lastFetchedQuery.current = inputValue
      }
      hasSuggestions.current = Boolean(filteredList.length)
      setExactMatch(
        filteredList.some(suggestion => isExactMatch(suggestion, inputValue))
      )
      return filteredList
    },
    [saleTransformer, barcodeScanErrorFix]
  )

  const onChangeHandler = useCallback(
    (suggestion: HydratedSuggestion | null, inputValue: string) => {
      setCurrentQuery(suggestion ? suggestion.name : '')

      if (barcodeScanErrorFix) {
        if (feelingLuckyHandledQuery.current === inputValue) {
          feelingLuckyHandledQuery.current = null
          return
        }
        feelingLuckyHandledQuery.current = null
      } else {
        if (feelingLuckyHandledQuery.current) {
          feelingLuckyHandledQuery.current = false
          return
        }
      }

      if (!suggestion && inputValue) {
        ToastNotifications.negative(
          <T
            _str="No results found for “{query}”. Try searching by product name."
            query={inputValue}
            _context={
              'Error message when searching for products or other entities'
            }
          />
        )
        // Add an unknown product.
        $rootScope.$emit('search:unknown-product', {
          [barcodeScanErrorFix ? 'query' : 'inputValue']: inputValue,
          suggestions: [],
        })
      }
      setExactMatch(false)
      onProductSelected(
        suggestion ? productSuggestionToDetails(suggestion) : null,
        inputValue
      )
    },
    [onProductSelected, barcodeScanErrorFix]
  )
  const feelingLuckyHandler = useCallback(
    async (inputValue: string) => {
      if (!feelingLucky) {
        return
      }

      const suggestionsAvailable = barcodeScanErrorFix
        ? lastFetchedQuery.current === inputValue && hasSuggestions.current
        : hasSuggestions.current

      const result = await feelingLucky(inputValue, suggestionsAvailable)

      if (result.handled) {
        feelingLuckyHandledQuery.current = barcodeScanErrorFix
          ? inputValue
          : true
      }

      if (!result.product) {
        if (barcodeScanErrorFix && result.handled) {
          // Return a simple object so the Autocomplete calls handleItemSelect
          // immediately, clearing the input and resetting isSubmitting.
          // Without this, the Autocomplete starts polling for the debounced
          // fetch to complete, keeping isSubmitting=true and blocking
          // subsequent Enter presses.
          return {
            id: '',
            name: '',
            type: 'product',
            product: {} as ApiProduct,
          }
        }
        return
      }

      const {
        variant_count,
        variant_options,
        variant_parent_id,
      } = result.product
      return {
        product: result.product,
        id: result.product.id,
        name: result.product.name,
        type:
          variant_count ||
          (Array.isArray(variant_options) && variant_options.length) ||
          variant_parent_id
            ? 'variant'
            : 'product',
      }
    },
    [feelingLucky, barcodeScanErrorFix]
  )

  const canUseLastItem = (
    product: HydratedSuggestion | null,
    query: string
  ) => {
    return barcodeScanErrorFix
      ? Boolean(query && product && product.name === query)
      : Boolean(product && product.name === query)
  }

  return (
    <Autocomplete
      selectedValue={currentQuery}
      fetchSuggestions={fetchSuggestions}
      feelingLucky={feelingLuckyHandler}
      onChange={onChangeHandler}
      renderSuggestion={suggestion => (
        <ProductSuggestion suggestion={suggestion} />
      )}
      leftIcon="fa fa-search"
      ref={forwardedRef}
      placeholder={t('Start typing or scanning', {
        _context: 'Placeholder for product search',
      })}
      renderError={networkError ? <NetworkError /> : null}
      testid="product-search"
      canSelectLastItem={canUseLastItem}
      actionBar={
        exactMatch || networkError
          ? null
          : ({ close }) => (
              <PopoverListFooter>
                <PopoverListFooterAction
                  onClick={() => {
                    close()
                    if (onAddNewProduct) {
                      onAddNewProduct(lastInputValue)
                    }
                  }}
                >
                  <Icon icon="fa-plus" className="vd-mr1" />
                  <T
                    _str="Add “{searchTerm}” as a new product."
                    _context="Button"
                    searchTerm={lastInputValue}
                  />
                </PopoverListFooterAction>
              </PopoverListFooter>
            )
      }
    />
  )
})
